// store.js
import { create } from "zustand";
import { persist } from "zustand/middleware";
import axios from "axios";

export const useAuthStore = create(
  persist(
    (set) => ({
  email: "",
  name: "",
  lastName: "",
  phone: "",
  isAuthenticated: false,
  emailVerified: false,
  userId: "",
  isAdmin: false,
  shouldSeeAdminPanel: false,
  authInitialized: false,
  lessons: [],
  passes: [],
  user: null,
  roles: [],
  isTrainer: false,
  isGongInstructor: false,
  trainerLessonTypes: [],
  setEmail: (email) => set({ email }),
  setEmailVerified: (emailVerified) => set({ emailVerified }),
  setPhone: (phone) => set({ phone }),
  setFirstName: (name) => set({ name }),
  setLastName: (lastName) => set({ lastName }),
  setAuthInitialized: (authInitialized) => set({ authInitialized }),
  setUserDetails: ({
    email,
    name,
    lastName,
    phone,
    isAuthenticated,
    userId,
    firebaseUid,
    isAdmin,
    shouldSeeAdminPanel,
  }) =>
    set({ 
      email, 
      name, 
      lastName, 
      phone, 
      isAuthenticated, 
      userId, 
      firebaseUid, 
      isAdmin: isAdmin || false,
      shouldSeeAdminPanel: shouldSeeAdminPanel || false
    }),
  setLessons: (lessons) => set({ lessons }),
  setPasses: (passes) => set({ passes: Array.isArray(passes) ? passes : [] }), // Ensure passes is an array
  setUser: (user) => set({ user }),
  addLesson: (lesson) =>
    set((state) => ({ lessons: [...state.lessons, lesson] })),
  clearUserDetails: () =>
    set({
      email: "",
      name: "",
      lastName: "",
      phone: "",
      isAuthenticated: false,
      userId: "",
      firebaseUid: "",
      isAdmin: false,
      shouldSeeAdminPanel: false,
      lessons: [],
      passes: [], // Clear passes when clearing user details
      user: null, // Clear Firebase user object
    }),
  fetchLessons: async () => {
    const { user } = useAuthStore.getState();
    if (!user) return;
    const idToken = await user.getIdToken();
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/user/me/lessons`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );
      set({ lessons: response.data.objects });
    } catch (error) {
      console.error("Failed to fetch lessons", error);
    }
  },
  fetchUserRoles: async () => {
    const { user } = useAuthStore.getState();
    if (!user) return;
    const idToken = await user.getIdToken();
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/user/me/roles`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );
      set({ 
        roles: response.data.roles || [],
        isTrainer: response.data.is_trainer || false,
        isGongInstructor: response.data.is_gong_instructor || false,
        shouldSeeAdminPanel: response.data.should_see_admin_panel || false,
      });
    } catch (error) {
      console.error("Failed to fetch user roles", error);
      set({ roles: [], isTrainer: false, isGongInstructor: false, shouldSeeAdminPanel: false });
    }
  },
  fetchTrainerLessonTypes: async () => {
    const { user, isTrainer, isGongInstructor } = useAuthStore.getState();
    if (!user || (!isTrainer && !isGongInstructor)) return;
    const idToken = await user.getIdToken();
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/trainers/me/lesson-types`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );
      set({ trainerLessonTypes: response.data.lesson_types || [] });
    } catch (error) {
      console.error("Failed to fetch trainer lesson types", error);
      set({ trainerLessonTypes: [] });
    }
  },
}),
    {
      name: "auth-storage", // unique name for localStorage key
      partialize: (state) => ({
        // Only persist authentication-related fields
        email: state.email,
        name: state.name,
        lastName: state.lastName,
        phone: state.phone,
        isAuthenticated: state.isAuthenticated,
        emailVerified: state.emailVerified,
        userId: state.userId,
        firebaseUid: state.firebaseUid,
        isAdmin: state.isAdmin,
        shouldSeeAdminPanel: state.shouldSeeAdminPanel,
        authInitialized: state.authInitialized,
        roles: state.roles,
        isTrainer: state.isTrainer,
        isGongInstructor: state.isGongInstructor,
        trainerLessonTypes: state.trainerLessonTypes,
      }),
    }
  )
);
